import {tx, ptx, usePageText} from '../lib/page-text';
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { CodeBlock } from '../components/ui';
import { EmbedIcon } from '../components/icons';

const PLAYER_BASE = 'https://kardocloud.com/players/srs_player.html';

export function EmbedPage() {
  usePageText();
  const streams = useQuery({ queryKey: ['my-streams'], queryFn: async () => (await api.get('/me/streams')).data });
  const [streamId, setStreamId] = useState<string>('');
  const [width, setWidth] = useState(640);
  const [height, setHeight] = useState(360);
  const [autoplay, setAutoplay] = useState(true);
  const [muted, setMuted] = useState(true); // most browsers require muted for autoplay

  const selected = streams.data?.find((s: any) => s.id === streamId) ?? streams.data?.[0];
  const key = selected?.keys?.[0]?.key as string | undefined;

  const playerUrl = useMemo(() => {
    if (!key) return '';
    const p = new URLSearchParams({ vhost: 'kardocloud.com', app: 'live', stream: key });
    if (autoplay) p.set('autostart', 'true');
    if (muted) p.set('muted', 'true');
    return `${PLAYER_BASE}?${p.toString()}`;
  }, [key, autoplay, muted]);

  const iframeSnippet = key ? `<iframe
  src="${playerUrl}"
  width="${width}"
  height="${height}"
  allow="autoplay; fullscreen; picture-in-picture"
  allowfullscreen
  frameborder="0"
></iframe>` : '';

  const responsiveSnippet = key ? `<div style="position:relative; padding-bottom:56.25%; height:0;">
  <iframe
    src="${playerUrl}"
    style="position:absolute; top:0; left:0; width:100%; height:100%;"
    allow="autoplay; fullscreen; picture-in-picture"
    allowfullscreen
    frameborder="0"
  ></iframe>
</div>`: '';

  const hlsJsSnippet = key ? `<video id="video" controls style="width:100%; max-width:${width}px;"></video>
<script src="https://cdn.jsdelivr.net/npm/hls.js@latest"></script>
<script>
  const video = document.getElementById('video');
  const src = 'https://kardocloud.com/live/${key}.m3u8';
  if (Hls.isSupported()) {
    const hls = new Hls();
    hls.loadSource(src);
    hls.attachMedia(video);
  } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
    video.src = src; // Safari native HLS
  }
  ${autoplay ? 'video.autoplay = true;' : ''}
  ${muted ? 'video.muted = true;' : ''}
</script>`: '';

  return (
    <div>
      <h1 className="h1">{tx("Embed widgets")}</h1>
      <p className="subtitle">{tx("Drop a Kardolive player into any website. Pick a stream, customize, copy the snippet.")}</p>

      {streams.data && streams.data.length === 0 ? (
        <div className="empty">
          <div className="icon-big"><EmbedIcon/></div>
          <h3>{tx("Create a stream first")}</h3>
          <p>{tx("You need at least one stream to generate an embed snippet.")}</p>
        </div>
      ) : (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <h3>{tx("Configure")}</h3>
            <div className="grid-2">
              <div>
                <label className="label">{tx("Stream")}</label>
                <select className="select" value={streamId || streams.data?.[0]?.id} onChange={(e) => setStreamId(e.target.value)}>
                  {streams.data?.map((s: any) => <option key={s.id} value={s.id}>{s.name} ({s.mode})</option>)}
                </select>
              </div>
              <div className="flex gap">
                <div style={{ flex: 1 }}>
                  <label className="label">{tx("Width")}</label>
                  <input className="input" type="number" min={320} max={1920} value={width} onChange={(e) => setWidth(+e.target.value)}/>
                </div>
                <div style={{ flex: 1 }}>
                  <label className="label">{tx("Height")}</label>
                  <input className="input" type="number" min={180} max={1080} value={height} onChange={(e) => setHeight(+e.target.value)}/>
                </div>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <div className="toggle-row">
                <div className="label-block"><span className="name">{tx("Autoplay on load")}</span><span className="desc">{tx("Browsers block autoplay unless muted; combine with the next toggle.")}</span></div>
                <button type="button" className={`toggle ${autoplay ? 'on' : ''}`} onClick={() => setAutoplay(!autoplay)}/>
              </div>
              <div className="toggle-row">
                <div className="label-block"><span className="name">{tx("Start muted")}</span><span className="desc">{tx("Recommended for autoplay-friendly embeds.")}</span></div>
                <button type="button" className={`toggle ${muted ? 'on' : ''}`} onClick={() => setMuted(!muted)}/>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3>{tx("Live preview")}</h3>
            {key ? (
              <iframe
                key={playerUrl}
                src={playerUrl}
                width={width}
                height={height}
                style={{ border: '1px solid var(--border)', borderRadius: 8, maxWidth: '100%' }}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            ) : <div className="muted">{tx("No stream selected")}</div>}
            <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>{tx("If the preview is blank, your selected stream isn't currently live.")}</p>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3>{tx("Fixed-size iframe")}</h3>
            <CodeBlock value={iframeSnippet}/>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3>{tx("Responsive (16:9)")}</h3>
            <p className="muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 12 }}>{tx("Scales to container width while preserving aspect ratio.")}</p>
            <CodeBlock value={responsiveSnippet}/>
          </div>

          <div className="card">
            <h3>{tx("hls.js (build your own player)")}</h3>
            <p className="muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 12 }}>{tx("Use this if you want full control over the player UI instead of the SRS-bundled one.")}</p>
            <CodeBlock value={hlsJsSnippet}/>
          </div>
        </>
      )}
    </div>
  );
}
